import { HERO_HEADING_FALLBACK } from "~/utils/marketingCopy";

type SeoDefinition = {
  title: string;
  description: string;
  ogType?: "website" | "article";
};

const PUBLIC_SEO_ROUTES: Array<{
  test: (path: string) => boolean;
  seo: SeoDefinition;
}> = [
  {
    test: (path) => path === "/",
    seo: {
      title: HERO_HEADING_FALLBACK,
      description:
        "Bon Apps builds custom software that replaces fragile spreadsheets and off-the-shelf tools for growing operations teams.",
    },
  },
  {
    test: (path) => path === "/contact",
    seo: {
      title: "Project enquiry",
      description:
        "Start a Bonapps project with a structured enquiry form so we can scope your workflow, priorities, and rollout plan.",
    },
  },
  {
    test: (path) => path === "/case-studies",
    seo: {
      title: "Case studies",
      description:
        "Explore Bonapps case studies and see how operational teams replaced manual systems with tailored applications.",
    },
  },
  {
    test: (path) => path.startsWith("/case-studies/"),
    seo: {
      title: "Case study",
      description:
        "Read a detailed Bonapps implementation case study covering outcomes, rollout approach, and operational impact.",
      ogType: "article",
    },
  },
  {
    test: (path) => path === "/app-suite",
    seo: {
      title: "App Suite by Bonapps",
      description:
        "Small apps for specific problems - Freelancer, Staff Tracker, RFI Manager, Get Ride Fit, and more.",
    },
  },
  {
    test: (path) => path === "/testimonials",
    seo: {
      title: "Client testimonials",
      description:
        "See what Bonapps clients say about the way we design, deliver, and support custom software in production.",
    },
  },
  {
    test: (path) => path === "/login",
    seo: {
      title: "Sign in",
      description:
        "Sign in to your Bon Apps account to access your workspace and project data.",
    },
  },
  {
    test: (path) => path === "/confirm",
    seo: {
      title: "Confirm account",
      description:
        "Confirm your Bon Apps account and continue to your workspace.",
    },
  },
  {
    test: (path) => path === "/new-password",
    seo: {
      title: "Set new password",
      description: "Set a new password for your Bon Apps account.",
    },
  },
  {
    test: (path) => path === "/reset-password",
    seo: {
      title: "Reset password",
      description: "Request a password reset link for your Bon Apps account.",
    },
  },
  {
    test: (path) => path === "/enquiries/login",
    seo: {
      title: "Enquiries sign in",
      description: "Sign in to Bon Apps enquiries tools.",
    },
  },
  {
    test: (path) => path === "/enquiries/new-password",
    seo: {
      title: "Enquiries new password",
      description: "Set a new password for your Bon Apps enquiries account.",
    },
  },
  {
    test: (path) => path === "/enquiries/reset-password",
    seo: {
      title: "Enquiries reset password",
      description:
        "Request a password reset for your Bon Apps enquiries account.",
    },
  },
];

const DEFAULT_PUBLIC_SEO: SeoDefinition = {
  title: "Bon Apps",
  description:
    "Bon Apps builds practical software for operations teams that need systems tailored to their real workflows.",
};

export default defineNuxtPlugin(() => {
  const router = useRouter();
  const runtimeConfig = useRuntimeConfig();

  const baseUrl = computed(() => {
    const configured = runtimeConfig.public.siteUrl;
    const fallback = "https://www.bonapps.app";
    return String(configured || fallback).replace(/\/+$/, "");
  });

  const normalizedPath = computed(() => router.currentRoute.value.path || "/");

  const routeSeo = computed(() => {
    const matched = PUBLIC_SEO_ROUTES.find((entry) =>
      entry.test(normalizedPath.value),
    );
    return matched?.seo ?? DEFAULT_PUBLIC_SEO;
  });

  const canonicalUrl = computed(
    () => `${baseUrl.value}${normalizedPath.value}`,
  );
  const ogImage = computed(() => `${baseUrl.value}/og/bonapps-default.svg`);

  useSeoMeta({
    title: () => routeSeo.value.title,
    description: () => routeSeo.value.description,
    ogTitle: () => routeSeo.value.title,
    ogDescription: () => routeSeo.value.description,
    ogImage: () => ogImage.value,
    ogUrl: () => canonicalUrl.value,
    ogType: () => routeSeo.value.ogType ?? "website",
    twitterCard: "summary_large_image",
  });

  useHead({
    link: [
      {
        rel: "canonical",
        href: () => canonicalUrl.value,
      },
    ],
  });
});
